import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Icon, Input, Modal } from '../design/index.js'

/**
 * "Customize Report Columns" (docs/10 §column chooser), and the same control on
 * every list.
 *
 * The choice is per person and per screen, and it is a preference rather than a
 * record: it lives in this browser. A saved view — which is shared, named and
 * belongs to the tenant — is a different feature and a different table.
 */

export interface ChoosableColumn {
  readonly key: string
  readonly label: string
}

const storageKey = (scope: string): string => `columns:${scope}`

const read = (scope: string): string[] | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(scope))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null
  } catch {
    // A private window, or storage turned off. The default columns are fine.
    return null
  }
}

const write = (scope: string, keys: string[] | null): void => {
  try {
    if (keys === null) window.localStorage.removeItem(storageKey(scope))
    else window.localStorage.setItem(storageKey(scope), JSON.stringify(keys))
  } catch {
    /* nothing to do: the screen still works, the choice just will not persist */
  }
}

export interface ChosenColumns<T extends ChoosableColumn> {
  /** The columns to render, in the definition's own order. */
  readonly visible: readonly T[]
  /** For the export URL. Empty when everything is shown. */
  readonly chosen: readonly string[]
  readonly isCustomised: boolean
  readonly choose: (keys: readonly string[]) => void
  readonly reset: () => void
}

export const useChosenColumns = <T extends ChoosableColumn>(
  scope: string, all: readonly T[],
): ChosenColumns<T> => {
  const [chosen, setChosen] = useState<readonly string[] | null>(null)

  // Read once the scope is known — the definition arrives asynchronously, so
  // the first render often has no columns to choose between yet.
  useEffect(() => {
    setChosen(scope ? read(scope) : null)
  }, [scope])

  const choose = useCallback((keys: readonly string[]) => {
    setChosen(keys)
    if (scope) write(scope, [...keys])
  }, [scope])

  const reset = useCallback(() => {
    setChosen(null)
    if (scope) write(scope, null)
  }, [scope])

  const visible = useMemo(() => {
    if (!chosen || chosen.length === 0) return all
    // In the CHOSEN order, not the definition's: the point of the right-hand
    // list is that somebody decided which column they read first.
    const byKey = new Map(all.map((c) => [c.key, c]))
    const kept = chosen.map((key) => byKey.get(key)).filter((c): c is T => c !== undefined)
    // Never leave a table with no columns at all.
    return kept.length > 0 ? kept : all
  }, [all, chosen])

  return {
    visible,
    chosen: chosen ?? [],
    isCustomised: Boolean(chosen && chosen.length > 0 && chosen.length < all.length),
    choose,
    reset,
  }
}

export interface ColumnsButtonProps<T extends ChoosableColumn> {
  readonly all: readonly T[]
  readonly state: ChosenColumns<T>
  readonly title?: string
}

export const ColumnsButton = <T extends ChoosableColumn>({
  all, state, title = 'Choose the columns',
}: ColumnsButtonProps<T>) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [dragging, setDragging] = useState<string | null>(null)

  const start = (): void => {
    setDraft(state.visible.map((c) => c.key))
    setSearch('')
    setOpen(true)
  }

  // Appended at the end, so ticking a column puts it where the eye expects —
  // last — rather than back wherever the definition happened to list it.
  const toggle = (key: string): void =>
    setDraft((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]))

  /** Moves one chosen column to a position, which is what sets its priority. */
  const move = (key: string | null, to: number): void => {
    if (key === null) return
    setDraft((current) => {
      const from = current.indexOf(key)
      if (from < 0 || to < 0 || to >= current.length) return current
      const next = [...current]
      next.splice(to, 0, ...next.splice(from, 1))
      return next
    })
  }

  const available = all.filter((c) =>
    search.trim() === '' || c.label.toLowerCase().includes(search.trim().toLowerCase()))

  return (
    <>
      <Button kind="ghost" icon={<Icon name="grid" size={18} />} onClick={start}>
        Columns{state.isCustomised ? ` (${state.visible.length})` : ''}
      </Button>

      {open ? (
        <Modal
          title={title}
          subtitle="Tick what you want to see, and drag to set the order they appear in."
          wide
          onClose={() => setOpen(false)}
          footerLeft={
            <Button
              kind="ghost"
              onClick={() => {
                state.reset()
                setOpen(false)
              }}
            >
              Show every column
            </Button>
          }
          actions={
            <>
              <Button kind="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={draft.length === 0}
                onClick={() => {
                  // Stored in the order the person put them in: the right-hand
                  // list IS the column order, which is what "sort the columns
                  // based on priority" means (docs/02 §3).
                  state.choose(draft)
                  setOpen(false)
                }}
              >
                Apply
              </Button>
            </>
          }
        >
          <div className="two-col">
            <div>
              <h3 className="subhead">Columns you can add</h3>
              {/*
                A catalogue runs to twenty-five columns on some documents, so it
                is searchable — scanning for "Tax Unit" in an unordered list of
                twenty-five is the reason nobody customises the view.
              */}
              <div style={{ marginBottom: 10 }}>
                <Input value={search} onChange={setSearch} placeholder="Search column name" />
              </div>
              <div className="column-choices">
                {available.map((column) => (
                  <label className="column-choice" key={column.key}>
                    <input
                      type="checkbox"
                      checked={draft.includes(column.key)}
                      onChange={() => toggle(column.key)}
                    />
                    <span>{column.label}</span>
                  </label>
                ))}
                {available.length === 0 ? <p className="helper">Nothing matches that.</p> : null}
              </div>
            </div>

            <div>
              <h3 className="subhead">Columns you have added</h3>
              <p className="helper">In the order they will appear. Drag a row to move it.</p>
              <ol className="chosen-columns">
                {draft.map((key, index) => {
                  const column = all.find((c) => c.key === key)
                  if (!column) return null
                  return (
                    <li
                      key={key}
                      className="chosen-column"
                      draggable
                      onDragStart={() => setDragging(key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault()
                        move(dragging, index)
                        setDragging(null)
                      }}
                    >
                      <span className="chosen-column__grip" aria-hidden="true">⠿</span>
                      <span className="chosen-column__name">{column.label}</span>
                      {/* Keyboard reordering, because a drag handle alone is not reachable. */}
                      <button
                        type="button"
                        className="linkish"
                        aria-label={`Move ${column.label} up`}
                        disabled={index === 0}
                        onClick={() => move(key, index - 1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="linkish"
                        aria-label={`Move ${column.label} down`}
                        disabled={index === draft.length - 1}
                        onClick={() => move(key, index + 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="linkish"
                        aria-label={`Remove ${column.label}`}
                        onClick={() => toggle(key)}
                      >
                        Remove
                      </button>
                    </li>
                  )
                })}
              </ol>
              {draft.length === 0 ? (
                <p className="muted" style={{ marginTop: 14 }}>Keep at least one column.</p>
              ) : null}
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  )
}

export interface ExportButtonProps {
  /** Where the workbook comes from; the browser downloads it directly. */
  readonly href: string
  readonly disabled?: boolean
  /** How many rows are about to leave, for the confirmation (docs/15 R-9.6). */
  readonly count?: number
  /** What is being exported, in the words of the screen: "purchase orders". */
  readonly what?: string
}

/**
 * `⭳ Export`, with the confirmation the source system asks for (R-9.6).
 *
 * The dialog is not ceremony. An export is the moment a filtered list becomes
 * a file that leaves the building, and the number in the sentence is what
 * catches "I meant this month" before the whole ledger is on somebody's laptop.
 *
 * The download itself is a plain link rather than a fetch: the file is streamed
 * with a Content-Disposition of its own, and the browser handles a 20,000-row
 * workbook better than a blob held in memory does.
 */
export const ExportButton = ({ href, disabled = false, count, what = 'entries' }: ExportButtonProps) => {
  const [asking, setAsking] = useState(false)

  return (
    <>
      <Button
        kind="ghost"
        icon={<Icon name="dl" size={18} />}
        disabled={disabled}
        onClick={() => setAsking(true)}
      >
        Export
      </Button>

      {asking ? (
        <Modal
          title="Export to Excel"
          tone="warn"
          actions={
            <>
              <Button kind="ghost" onClick={() => setAsking(false)}>Cancel</Button>
              <Button
                kind="primary"
                onClick={() => {
                  setAsking(false)
                  window.location.href = href
                }}
              >
                Yes, continue
              </Button>
            </>
          }
          onClose={() => setAsking(false)}
        >
          <p>
            This will export{' '}
            {count === undefined ? `every one of the ${what} on this screen` : `all ${count} ${what}`}
            {' '}to Excel. Are you sure you want to continue?
          </p>
          <p className="helper">
            The file carries an Export Info sheet naming this company, you, and the time — so a
            spreadsheet found later can be traced back.
          </p>
        </Modal>
      ) : null}
    </>
  )
}
